import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, HelpCircle, Plus } from 'lucide-react';
import { ContributeButton } from '@/components/cars/ContributeButton';
import {
  getRelevantFields,
  isFieldEmpty,
  type MissingFieldDef,
} from '@/constants/missing-fields';
import { EQUIPMENT_FIELD_KEYS } from '@/constants/equipment-fields';
import type { Tables } from '@/integrations/supabase/types';

type Variant = Tables<'variants'>;
type Specs = Tables<'powertrain_specs'>;

interface Props {
  variant: Variant;
  specs: Specs | null | undefined;
  hasPrice: boolean;
  /** Triggers the multi-variant dialog with the given field highlighted. */
  onChipClick: (fieldKey: string) => void;
}

/**
 * Kompakt bidragssektion: antal mangler, kort forklaring, én CTA og en
 * fold-ud-liste med felterne. Sekundært indhold — må ikke konkurrere med bildata.
 */
export function MissingFieldsBox({ variant, specs, hasPrice, onChipClick }: Props) {
  const [open, setOpen] = useState(false);

  const missing = useMemo<MissingFieldDef[]>(() => {
    const all = getRelevantFields(variant.powertrain_type);
    return all.filter(f => {
      if (EQUIPMENT_FIELD_KEYS.has(f.key)) return false;
      if (f.scope === 'variant') return isFieldEmpty((variant as Record<string, unknown>)[f.key]);
      if (f.scope === 'specs') return !specs || isFieldEmpty((specs as Record<string, unknown>)[f.key]);
      if (f.scope === 'price') return !hasPrice;
      return false;
    });
  }, [variant, specs, hasPrice]);

  if (missing.length === 0) return null;

  const firstKey = missing[0]?.key;

  return (
    <section className="rounded-xl border border-border bg-muted/40 px-4 py-3 sm:px-5 sm:py-4">
      <div className="flex items-start gap-2.5">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold leading-tight">
            {missing.length} {missing.length === 1 ? 'oplysning mangler' : 'oplysninger mangler'}
          </h3>
          <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
            Kender du tallene? Send et forslag — en admin godkender det, før det vises.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {firstKey && (
              <ContributeButton icon="plus" onClick={() => onChipClick(firstKey)} />
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              className="h-9 px-2 text-[13px] text-muted-foreground hover:text-foreground"
            >
              {open ? 'Skjul felter' : 'Se hvilke felter'}
              <ChevronDown
                className={`ml-1 h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </Button>
          </div>

          {open && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {missing.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => onChipClick(f.key)}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-[13px] transition-colors hover:border-accent hover:bg-accent/10"
                >
                  <Plus className="h-3 w-3 text-accent" aria-hidden />
                  <span>{f.label}</span>
                  {f.unit && <span className="text-xs text-muted-foreground">({f.unit})</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
